import { Bot, Loader2, RefreshCw, Sparkles } from "lucide-react";
import { useParams } from "react-router-dom";

import { useConfirm } from "@/shared/hooks/use-confirm";
import { Button } from "@/shared/ui/button";
import { CARD_WALL_RADIUS } from "@/shared/ui/cardWall";
import { QueryErrorBanner } from "@/shared/ui/QueryErrorBanner";
import { Skeleton } from "@/shared/ui/skeleton";
import { cn } from "@/shared/lib/utils";

import { websiteService } from "../api/websiteService";
import { circled, missingRequiredFacts } from "../model/collect";
import { goalLabel } from "../model/goalLabels";
import { absoluteHostUrl, isHostSite } from "../model/hostNodes";
import { ChecklistPanel } from "./components/ChecklistPanel";
import { QuestionCard } from "./components/QuestionCard";
import { useCollectFlow } from "./hooks/useCollectFlow";
import { SiteShell } from "./SiteShell";

/**
 * 屏 3「资料采集」:左 = 追问流(AI 首条气泡 + 每题一张卡 + 答完的题变成用户气泡),右 = 清单对账;
 * 页头 = 重新预填 + 生成初稿两枚按钮。数据流(加载 / 预填 / 答题 / 出稿、串行、防串台)在 `useCollectFlow`。
 *
 * ★ 进页清单是「刚建好还没读过知识库」的形状(`needsPrefill`)才自动预填一次;页头「重新预填」常驻,
 *   先确认再跑(api 合并时不动 confirmed / excluded / 用户手填的项,确认框照此说话)。
 * ★ 题目画的就是最新清单里的 `questions`(减去本地跳过的):api 已去掉答完的题,还给同一个 id 就再出卡。
 *   同一 id 第 n 次出卡用新 key,题卡里上一轮的勾选 / 输入不带过来。
 * ★ 任一写口(预填 / 答题 / 出稿)在路上时,所有题卡、「重新预填」、两枚出稿按钮一起禁用(串行提交)。
 * ★ 文字题只在题卡里答;底部只留「上传资料」(禁用,说明原因)和一句提示,不再有第二个输入框。
 * ★ 「生成初稿」在任一「必填 + 事实 + 缺失」项存在时禁用(title 点名);「先生成初稿,缺的留占位」
 *   不看缺失(allowPlaceholders:true,占位在发布前校验会拦,附录 A.2)。
 * ★ 新到的题由一个 `role="status"`(polite)的读屏区报出来:「下一题:…」。
 * ★ 副标带「主目标:…」(站点已有 `businessGoal` 时,码 → 中文走 goalLabels);答完目标题后 flow 重拉站点。
 * ★ 0925 B7:右栏清单每一可采集行都能直接改(不依赖追问流),保存走同一个 answers 端点、同一把串行锁;
 *   图片行复用图片上传端点(A.53,只在 host 模式可用)。
 */
export default function CollectPage() {
  const { id = "" } = useParams<{ id: string }>();
  const confirm = useConfirm();
  const flow = useCollectFlow(id);
  const { site, checklist, fields, error, busy, answeringId, answered, skipped } = flow;

  const rePrefill = async () => {
    const ok = await confirm({
      title: "重新从知识库预填?",
      description: "已确认的内容不会被覆盖。",
      confirmText: "重新预填",
    });
    if (ok) await flow.runPrefill();
  };

  if (error) {
    return <QueryErrorBanner isError message={error} onRetry={() => void flow.load()} />;
  }
  if (!site || !checklist) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-2/3" />
        <Skeleton className="h-6 w-1/2" />
        <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px]">
          <Skeleton className="h-96 w-full" />
          <Skeleton className="h-96 w-full" />
        </div>
      </div>
    );
  }

  const locked = busy !== null;
  const { done, total } = checklist.progress;
  const remaining = Math.max(total - done, 0);
  const missing = missingRequiredFacts(checklist.groups);
  const pending = checklist.questions.filter((q) => !skipped.has(q.id));
  const timesAnswered = (qid: string) => answered.filter((a) => a.question.id === qid).length;
  const current = pending[0] ?? null;
  const goal = goalLabel(site.businessGoal);
  const announce =
    busy === "prefill"
      ? "正在读知识库预填…"
      : current
        ? `下一题:${current.text}`
        : remaining > 0
          ? "题都答完了,右边清单里标了还缺什么。"
          : "资料已经齐了,可以生成初稿。";

  return (
    <SiteShell
      site={site}
      pageTitle="资料采集"
      subtitle={`模板 ${site.templateName}${goal ? ` · 主目标:${goal}` : ""} · 已从知识库预填 ${done} / ${total} 项${
        remaining > 0 ? `,还有 ${remaining} 项要你确认或补充` : ""
      }`}
      actions={
        <>
          <Button variant="outline" size="sm" disabled={locked} onClick={() => void rePrefill()}>
            {busy === "prefill" ? <Loader2 className="animate-spin" /> : <RefreshCw />}
            重新预填
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={locked}
            onClick={() => void flow.generate(true)}
          >
            先生成初稿,缺的留占位
          </Button>
          <Button
            size="sm"
            disabled={locked || missing.length > 0}
            title={missing.length > 0 ? `还缺必填事实字段:${missing.join("、")}` : undefined}
            onClick={() => void flow.generate(false)}
          >
            {busy === "generate" ? <Loader2 className="animate-spin" /> : <Sparkles />}
            生成初稿
          </Button>
        </>
      }
    >
      {/* items-start:清单一长(repeat 组按实例展开),左边对话卡不跟着拉高、底栏不掉到几屏之外。 */}
      <div className="grid gap-6 lg:grid-cols-[minmax(0,1fr)_360px] lg:items-start">
        <section
          aria-label="追问"
          className={cn("flex flex-col border border-border bg-card", CARD_WALL_RADIUS)}
        >
          <p role="status" className="sr-only">
            {announce}
          </p>
          <ol aria-label="对话" className="flex-1 space-y-4 p-4">
            <li className="flex items-start gap-3">
              <BotAvatar />
              <div className="max-w-prose space-y-1 rounded-lg bg-muted/50 px-3 py-2 text-sm text-foreground">
                <p>
                  我读了知识库里的资料,能确定的 {done}{" "}
                  项已经填好,每项都标了出处,你可以在右边逐条核对。
                </p>
                {busy === "prefill" ? (
                  <p className="text-muted-foreground">正在读知识库预填…</p>
                ) : remaining > 0 ? (
                  <p>
                    还有 {remaining} 项要你确认或补充
                    {pending.length > 0 ? ",下面我一项项问你。" : ",右边清单里标了缺什么。"}
                  </p>
                ) : (
                  <p>资料已经齐了,可以生成初稿。</p>
                )}
              </div>
            </li>

            {answered.map((a, i) => (
              <li key={a.key} className="space-y-2">
                <div className="flex items-start gap-3">
                  <BotAvatar />
                  <div className="max-w-prose rounded-lg bg-muted/50 px-3 py-2 text-sm text-foreground">
                    <span className="mr-1 text-primary">{circled(i)}</span>
                    {a.question.text}
                  </div>
                </div>
                <div className="flex justify-end">
                  <div
                    aria-label="你的回答"
                    className="max-w-prose rounded-lg bg-primary px-3 py-2 text-sm text-primary-foreground"
                  >
                    {a.labels.join("、")}
                  </div>
                </div>
              </li>
            ))}

            {pending.map((q, i) => (
              <li key={`${q.id}#${timesAnswered(q.id)}`}>
                <QuestionCard
                  index={answered.length + i}
                  question={q}
                  submitting={answeringId === q.id}
                  disabled={locked}
                  onAnswer={(a) => void flow.answer(q, a)}
                  onSkip={() => flow.skip(q.id)}
                />
              </li>
            ))}
          </ol>

          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border/60 p-3">
            <p className="text-2xs text-muted-foreground">
              选择题点选项;要写字的题直接写在题卡里。
            </p>
            {/* ★0926:原「上传资料」按钮长期 disabled(上传走资产库,本期不做)—— 灰按钮只会让人
                以为坏了,改成一句说明资料从哪来。 */}
            <p data-testid="collect-source-hint" className="text-2xs text-muted-foreground">
              资料从知识库读取;要补资料请先传进知识库。
            </p>
          </div>
        </section>

        <ChecklistPanel
          checklist={checklist}
          fields={fields}
          editing={{
            onSave: flow.editRow,
            upload: async (target, data) => {
              const { url } = await websiteService.uploadImage(id, { data, ...target });
              return absoluteHostUrl(url);
            },
            hostMode: isHostSite(site),
            disabled: locked,
          }}
        />
      </div>
    </SiteShell>
  );
}

function BotAvatar() {
  return (
    <span
      aria-hidden
      className="grid h-7 w-7 shrink-0 place-items-center rounded-full bg-primary/10 text-primary"
    >
      <Bot className="h-4 w-4" />
    </span>
  );
}
